import { Trans } from "react-i18next";
import React, { useState } from "react";
import {
  Card,
  Button,
  Form,
  Badge,
  ListGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { FaTrash } from "react-icons/fa";
import { FaEdit } from "react-icons/fa";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaClock } from "react-icons/fa";
import "../../../shared/styles/annotation-tools.css";
import "./AnnotationTools.css";

export interface Annotation {
  id: string;
  type:
    | "event"
    | "seizure-onset"
    | "seizure-offset"
    | "artifact"
    | "sleep-stage"
    | "note";
  timestamp: number;
  endTimestamp?: number;
  label: string;
  description?: string;
  createdBy: string;
  createdAt: Date;
  color: string;
}

interface AnnotationToolsProps {
  annotations: Annotation[];
  currentTime: number;
  onAddAnnotation: (annotation: Omit<Annotation, "id" | "createdAt">) => void;
  onEditAnnotation: (id: string, updates: Partial<Annotation>) => void;
  onDeleteAnnotation: (id: string) => void;
  onJumpToAnnotation: (timestamp: number) => void;
}

const AnnotationTools: React.FC<AnnotationToolsProps> = ({
  annotations,
  currentTime,
  onAddAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  onJumpToAnnotation,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: "event" as Annotation["type"],
    label: "",
    description: "",
    duration: 0,
  });

  const annotationTypes = [
    {
      value: "event",
      label: "Event Marker",
      color: "#1f77b4",
      icon: <FaMapMarkerAlt />,
    },
    {
      value: "seizure-onset",
      label: "Seizure Onset",
      color: "#d62728",
      icon: <FaClock />,
    },
    {
      value: "seizure-offset",
      label: "Seizure Offset",
      color: "#ff7f0e",
      icon: <FaClock />,
    },
    {
      value: "artifact",
      label: "Artifact",
      color: "#8c564b",
      icon: <FaMapMarkerAlt />,
    },
    {
      value: "sleep-stage",
      label: "Sleep Stage",
      color: "#9467bd",
      icon: <FaMapMarkerAlt />,
    },
    {
      value: "note",
      label: "Free Text Note",
      color: "#2ca02c",
      icon: <FaEdit />,
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const typeConfig = annotationTypes.find((t) => t.value === formData.type);
    const newAnnotation = {
      type: formData.type,
      timestamp: currentTime,
      endTimestamp:
        formData.duration > 0 ? currentTime + formData.duration : undefined,
      label: formData.label,
      description: formData.description || undefined,
      createdBy: "Current User", // In real app, get from auth context
      color: typeConfig?.color || "#1f77b4",
    };

    if (editingId) {
      onEditAnnotation(editingId, newAnnotation);
      setEditingId(null);
    } else {
      onAddAnnotation(newAnnotation);
    }

    // Reset form
    setFormData({ type: "event", label: "", description: "", duration: 0 });
    setShowAddForm(false);
  };

  const handleEdit = (annotation: Annotation) => {
    setFormData({
      type: annotation.type,
      label: annotation.label,
      description: annotation.description || "",
      duration: annotation.endTimestamp
        ? annotation.endTimestamp - annotation.timestamp
        : 0,
    });
    setEditingId(annotation.id);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setFormData({ type: "event", label: "", description: "", duration: 0 });
    setEditingId(null);
    setShowAddForm(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTypeConfig = (type: string) => {
    return annotationTypes.find((t) => t.value === type) || annotationTypes[0];
  };

  return (
    <Card className="annotation-tools shadow-sm">
      <Card.Body>
        <Card.Title className="d-flex justify-content-between align-items-center mb-3">
          <span>
            <Trans ns="features" i18nKey="ui.copy.228df13ea8e3" /><Badge bg="secondary" className="ms-2">
              {annotations.length}
            </Badge>
          </span>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setShowAddForm(!showAddForm)}
            aria-label="Add new annotation"
          >
            <FaPlus className="me-1" /> <Trans ns="features" i18nKey="ui.copy.61cc55aa0453" /></Button>
        </Card.Title>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="mb-3 annotation-form-card">
            <Card.Body>
              <h6 className="mb-3">
                {editingId ? "Edit Annotation" : "New Annotation"}
              </h6>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label><Trans ns="features" i18nKey="ui.copy.3deb74565196" /></Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type: e.target.value as Annotation["type"],
                      })
                    }
                    required
                  >
                    {annotationTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><Trans ns="features" i18nKey="ui.copy.74341e3c271d" /></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Generalized spike-wave"
                    value={formData.label}
                    onChange={(e) =>
                      setFormData({ ...formData, label: e.target.value })
                    }
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><Trans ns="features" i18nKey="ui.copy.f1da5c02d4ea" /></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Additional notes..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><Trans ns="features" i18nKey="ui.copy.1fe25f7d03c1" /></Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={0.1}
                    placeholder="0 for point marker"
                    value={formData.duration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        duration: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                  <Form.Text className="text-muted">
                    <Trans ns="features" i18nKey="ui.copy.705660ff841e" /></Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button type="submit" size="sm" variant="primary">
                    {editingId ? "Update" : "Add"} <Trans ns="features" i18nKey="ui.copy.27e90dfa57c3" />{formatTime(currentTime)}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleCancel}
                  >
                    <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" /></Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {/* Annotations List */}
        <div className="annotations-list">
          {annotations.length === 0 ? (
            <div className="text-center text-muted py-4">
              <p><Trans ns="features" i18nKey="ui.copy.d80a7fb21d99" /></p>
              <small><Trans ns="features" i18nKey="ui.copy.a13d325bb2ea" /></small>
            </div>
          ) : (
            <ListGroup variant="flush">
              {annotations
                .sort((a, b) => a.timestamp - b.timestamp)
                .map((annotation) => {
                  const typeConfig = getTypeConfig(annotation.type);
                  return (
                    <ListGroup.Item
                      key={annotation.id}
                      className="annotation-item"
                      style={
                        {
                          "--annotation-color": typeConfig.color,
                        } as React.CSSProperties
                      }
                    >
                      <div className="annotation-header">
                        <div className="annotation-type">
                          <span
                            className="annotation-type__icon"
                            style={
                              {
                                "--annotation-color": typeConfig.color,
                              } as React.CSSProperties
                            }
                          >
                            {typeConfig.icon}
                          </span>
                          <Badge
                            bg="light"
                            text="dark"
                            className="ms-2 annotation-type__badge"
                            style={
                              {
                                "--annotation-color": typeConfig.color,
                              } as React.CSSProperties
                            }
                          >
                            {typeConfig.label}
                          </Badge>
                        </div>
                        <div className="annotation-actions">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                <Trans ns="features" i18nKey="ui.copy.cfd6869bbed1" />{formatTime(annotation.timestamp)}
                              </Tooltip>
                            }
                          >
                            <Button
                              size="sm"
                              variant="link"
                              onClick={() =>
                                onJumpToAnnotation(annotation.timestamp)
                              }
                              className="p-0 me-2"
                              aria-label={`Jump to annotation at ${formatTime(annotation.timestamp)}`}
                            >
                              <FaClock />
                            </Button>
                          </OverlayTrigger>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => handleEdit(annotation)}
                            className="p-0 me-2"
                            aria-label="Edit annotation"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            className="text-danger p-0"
                            onClick={() => onDeleteAnnotation(annotation.id)}
                            aria-label="Delete annotation"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                      <div className="annotation-content">
                        <strong className="annotation-label">
                          {annotation.label}
                        </strong>
                        <div className="annotation-time">
                          {formatTime(annotation.timestamp)}
                          {annotation.endTimestamp && (
                            <>
                              {" "}
                              – {formatTime(annotation.endTimestamp)}
                              <span className="text-muted">
                                (
                                {(
                                  annotation.endTimestamp - annotation.timestamp
                                ).toFixed(1)}
                                <Trans ns="features" i18nKey="ui.copy.ecda5eecde4a" /></span>
                            </>
                          )}
                        </div>
                        {annotation.description && (
                          <p className="annotation-description">
                            {annotation.description}
                          </p>
                        )}
                        <small className="text-muted">
                          <Trans ns="features" i18nKey="ui.copy.cfadbd7a5771" />{annotation.createdBy} •{" "}
                          {annotation.createdAt.toLocaleDateString()}
                        </small>
                      </div>
                    </ListGroup.Item>
                  );
                })}
            </ListGroup>
          )}
        </div>

        {/* Quick Add Buttons */}
        <div className="quick-add-section mt-3">
          <small className="text-muted d-block mb-2"><Trans ns="features" i18nKey="ui.copy.b1909497499a" /></small>
          <div className="quick-add-buttons">
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => {
                onAddAnnotation({
                  type: "seizure-onset",
                  timestamp: currentTime,
                  label: "Seizure Onset",
                  createdBy: "Current User",
                  color: "#d62728",
                });
              }}
            >
              <Trans ns="features" i18nKey="ui.copy.fa91af776886" /></Button>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={() => {
                onAddAnnotation({
                  type: "seizure-offset",
                  timestamp: currentTime,
                  label: "Seizure Offset",
                  createdBy: "Current User",
                  color: "#ff7f0e",
                });
              }}
            >
              <Trans ns="features" i18nKey="ui.copy.349861a1f2a8" /></Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                onAddAnnotation({
                  type: "artifact",
                  timestamp: currentTime,
                  label: "Artifact",
                  createdBy: "Current User",
                  color: "#8c564b",
                });
              }}
            >
              <Trans ns="features" i18nKey="ui.copy.aa778b50a158" /></Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AnnotationTools;
